import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ThemeButton } from '@shared/ui/buttons';
import {
  setTransactionsParams,
  resetTransactionsFilters,
} from '@app/store/slices/transactionsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { parseDateQuery, formatDateQuery } from '@utils/date-helpers';
import { transactionStatuses } from '@utils/statuses';
import { groupTeamsByStatus } from '@utils/filters-helpers';
import { fetchBankWallets } from '@app/store/slices/bankWalletsSlice';
import { useEffect, useMemo } from 'react';
import { fetchAllGroups } from '@app/store/slices/myTeamSlice';
import { roles } from '@shared/static/roles';
import {
  BasicSelectInput,
  DateRangeInput,
  BankSelect,
} from '@shared/ui/forms/inputs';
import { RenderItemTeam } from '@shared/ui/forms/select-render';

export default function TransactionsFilter({ close }) {
  const { t } = useTranslation();
  const { params } = useSelector((state) => state.transactions);
  const { allGroupsWithDeleted } = useSelector((state) => state.my_team);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const isMember = user?.group_role === roles.TEAM_MEMBER;
  const { bankWallets } = useSelector((state) => state.bankWallets);
  const allGroupsWithDeletedGrouped = useMemo(
    () => groupTeamsByStatus(allGroupsWithDeleted, t),
    [allGroupsWithDeleted, t],
  );

  useEffect(() => {
    dispatch(fetchBankWallets());
    if (!isMember) {
      dispatch(fetchAllGroups());
    }
  }, []);

  const { control, handleSubmit } = useForm({
    defaultValues: {
      bank_id: params.bank_id || null,
      group_id: params.group_id || null,
      order_status: params.order_status || null,
      operationDate:
        params.start_date && params.end_date
          ? [parseDateQuery(params.start_date), parseDateQuery(params.end_date)]
          : null,
    },
  });

  function onSubmit(formData) {
    const start_date = formatDateQuery(formData.operationDate?.[0]);
    const end_date = formatDateQuery(formData.operationDate?.[1]);
    dispatch(
      setTransactionsParams({
        page: 1,
        start_date,
        end_date,
        bank_id: formData.bank_id,
        group_id: formData.group_id,
        order_status: formData.order_status,
      }),
    );
    close();
  }

  function handleReset() {
    dispatch(resetTransactionsFilters());
    close();
  }

  return (
    <>
      <div className="block-pd-x drawerFilter-grow">
        <div className="form">
          <BankSelect
            control={control}
            name="bank_id"
            label={t('bank_bin')}
            data={bankWallets}
          />
          {!isMember && (
            <BasicSelectInput
              control={control}
              name="group_id"
              label={t('team')}
              placeholder={t('select_team')}
              Render={RenderItemTeam}
              searchable
              data={allGroupsWithDeletedGrouped}
              groupBy="group"
            />
          )}
          <BasicSelectInput
            control={control}
            name="order_status"
            label={t('status')}
            data={transactionStatuses}
            valueKey="value"
            labelKey="label"
          />
          <DateRangeInput
            control={control}
            name="operationDate"
            label={t('transaction_date')}
          />
        </div>
      </div>
      <div className="btns-toolbar dialog-actions">
        <ThemeButton startIcon="check" onClick={handleSubmit(onSubmit)}>
          {t('apply')}
        </ThemeButton>
        <ThemeButton appearance="secondary" onClick={handleReset}>
          {t('reset')}
        </ThemeButton>
      </div>
    </>
  );
}
